import React, { useCallback } from 'react';
import { flexRender, HeaderGroup } from '@tanstack/react-table';
import { OrderEntryTableRow } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-row';
import { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { StyleSheet, View } from 'react-native';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useTheme } from '@/contexts/theme-context';

interface UseOrderEntryTableRenderingProps {
  /**
   * Invoked when a row's background is pressed. Receives the row's data so
   * consumers can decide what to do (e.g. open the item detail side panel
   * for the row's variant pack).
   */
  onRowPress?: (rowData: OrderEntryTableRowData) => void;
}

interface UseOrderEntryTableRenderingResult {
  renderHeaderRow: (props: {
    headerGroup: HeaderGroup<OrderEntryTableRowData>;
  }) => React.ReactNode;
  renderRow: (props: {
    index: number;
    row: { id: string; original: OrderEntryTableRowData };
  }) => React.ReactElement;
}

/**
 * Rendering callbacks for order entry table rows and headers.
 *
 * Separated from column configuration so each concern can evolve independently.
 *
 * @param props.onRowPress - Optional handler fired on row-background press.
 */
export const useOrderEntryTableRendering = ({
  onRowPress,
}: UseOrderEntryTableRenderingProps = {}): UseOrderEntryTableRenderingResult => {
  const theme = useTheme();

  /** Renders a sortable header row with pinning support. */
  const renderHeaderRow = useCallback(
    ({ headerGroup }: { headerGroup: HeaderGroup<OrderEntryTableRowData> }) => (
      <TableHeaderRow key={headerGroup.id}>
        {headerGroup.headers.map((header) => {
          return (
            <TableHeaderCell
              columnId={header.id}
              key={header.id}
              onPress={
                header.column.getCanSort()
                  ? () => header.column.toggleSorting()
                  : undefined
              }
              pinnedOffset={
                header.column.getIsPinned()
                  ? header.column.getStart('left')
                  : undefined
              }
              sortDirection={header.column.getIsSorted() || undefined}
            >
              {header.isPlaceholder ? null : (
                <View style={styles.columnHeaderContainer}>
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    variant="paragraph-sm-600"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </Typography>
                </View>
              )}
            </TableHeaderCell>
          );
        })}
      </TableHeaderRow>
    ),
    [theme.sk.color.text.tertiary],
  );

  /** Renders a data row, delegating to OrderEntryTableRow. */
  const renderRow = useCallback(
    ({
      index,
      row,
    }: {
      index: number;
      row: { id: string; original: OrderEntryTableRowData };
    }) => {
      return (
        <OrderEntryTableRow
          index={index}
          key={row.id}
          onRowPress={onRowPress}
          row={row as Parameters<typeof OrderEntryTableRow>[0]['row']}
        />
      );
    },
    [onRowPress],
  );

  return { renderHeaderRow, renderRow };
};

const styles = StyleSheet.create({
  columnHeaderContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 1,
    minWidth: 0,
  },
});
